import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { X } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useSanityContext } from '@/sanity/SanityDataProvider';

/**
 * R2 - Props interface for WhatsAppButton component
 * All props have sensible defaults for immediate use
 */
interface WhatsAppButtonProps {
  /** WhatsApp phone number (without + or spaces) */
  phoneNumber?: string;
  /** Pre-filled message for the chat */
  message?: string;
  /** Tooltip text shown to attract user attention */
  tooltipText?: string;
  /** Delay in ms before showing the tooltip automatically */
  tooltipDelay?: number;
}

/**
 * R3 - Animation variants separated for clean JSX
 * Tooltip entrance/exit animations
 */
const tooltipVariants: Variants = {
  hidden: { 
    opacity: 0, 
    y: 10, 
    scale: 0.9 
  },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { 
      type: 'spring', 
      stiffness: 300, 
      damping: 20 
    }
  },
  exit: { 
    opacity: 0, 
    y: 10, 
    scale: 0.9,
    transition: { 
      duration: 0.2 
    }
  }
};

/**
 * R3 - Button pulse animation for attention
 */
const pulseVariants: Variants = {
  pulse: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      repeatDelay: 3
    }
  }
};

/**
 * R2 - Reusable Floating Action Button for WhatsApp
 * R3 - Well-documented with JSDoc and clean separation of concerns
 * R4 - Optimized for touch targets (min 48x48px) and high z-index
 * 
 * @example
 * // Basic usage with defaults
 * <WhatsAppButton />
 * 
 * @example
 * // Custom configuration
 * <WhatsAppButton 
 *   phoneNumber="34612345678"
 *   message="Hola, necesito información"
 *   tooltipText="¿Te ayudamos?"
 * />
 */
export function WhatsAppButton({
  phoneNumber,
  message,
  tooltipText = '👋 ¿Podemos ayudarte con tu proyecto?',
  tooltipDelay = 3000
}: WhatsAppButtonProps) {
  // R4 - Use Sanity contactInfo as source of truth, then prop overrides, then defaults
  const { data } = useSanityContext();
  const ci = data?.contactInfo;
  const resolvedPhone = phoneNumber ?? ci?.whatsappNumber ?? '34612345678';
  const resolvedMessage = message ?? ci?.whatsappDefaultMessage ?? 'Hola INBOCA, quiero transformar mi espacio corporativo.';
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  /**
   * R1 - Robust timer handling with cleanup
   * Prevents memory leaks if component unmounts before timer fires
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isDismissed) {
        setShowTooltip(true);
      }
    }, tooltipDelay);

    return () => clearTimeout(timer);
  }, [tooltipDelay, isDismissed]);

  /**
   * R1 - Safe URL construction with proper encoding
   * encodeURIComponent prevents XSS and handles special characters
   */
  const whatsappUrl = `https://wa.me/${resolvedPhone}?text=${encodeURIComponent(resolvedMessage)}`;

  /**
   * R1 - Memoized dismiss handler
   */
  const handleDismiss = useCallback(() => {
    setShowTooltip(false);
    setIsDismissed(true);
  }, []);

  const isTooltipVisible = (showTooltip || isHovered) && !isDismissed;

  return (
    <div 
      className="fixed bottom-6 right-6 z-[100] flex flex-col items-end gap-3 pointer-events-none"
      role="complementary"
      aria-label="WhatsApp contact widget"
    >
      {/* R4 - Tooltip with AnimatePresence for smooth enter/exit */}
      <AnimatePresence mode="wait">
        {isTooltipVisible && (
          <motion.div
            variants={tooltipVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="pointer-events-auto bg-white text-slate-800 p-4 rounded-2xl rounded-br-sm shadow-xl border border-slate-100 max-w-[250px] relative"
          >
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"
              aria-label="Cerrar mensaje"
              type="button"
            >
              <X size={14} />
            </button>

            {/* Tooltip content */}
            <p className="text-sm font-medium leading-snug pr-4">
              {tooltipText}
            </p>

            {/* Chat bubble tail */}
            <div className="absolute -bottom-2 right-4 w-4 h-4 bg-white border-r border-b border-slate-100 transform rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* R4 - Main FAB with touch-friendly size (56x56px > 48px minimum) */}
      <motion.a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="pointer-events-auto bg-[#25D366] hover:bg-[#20BD5A] text-white p-4 rounded-full shadow-lg shadow-green-500/30 flex items-center justify-center min-w-[56px] min-h-[56px] transition-colors"
        variants={pulseVariants}
        animate="pulse"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label="Contactar por WhatsApp"
      >
        <FaWhatsapp size={28} />
      </motion.a>
    </div>
  );
}
